import {Component, Input} from "@angular/core";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {CustomerService} from "../../customer/customer-service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {Ms4CustomerModel, ms4CustomerModelFactory} from "../../customer/customer.model";
import {AppSettingsService} from "../../app-settings/app-settings.service";
import { SearchSoldtoInOtherCountriesInfoComponent } from "./search-soldto-in-other-countries-info.component";
import { TARGET_SYSTEM } from "../../helpers/app-constants";

export interface FindCustomerItsmInputModel {
  customerName: string;
  projectId: string;
  isReseller: boolean;
  customerSearchBy ?: string;
  targetSystem: string;
  isLeadCustomer?: boolean;
  otherCountriesSearch?: boolean;
}

export interface FindCustomerItsmResultsModel {
  selectedCustomerInfo: Ms4CustomerModel;
  customerSearchBy:string;
  customerName:string;
  otherCountriesSearch?: boolean
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./project-details-find-customer-dialog.template.pug",
  providers: [
    CustomerService,
  ],
})
@exposeComponentInDebugMode()
export class FindCustomerITSMDialogComponent
  extends DialogComponent<FindCustomerItsmInputModel, FindCustomerItsmResultsModel>
  implements FindCustomerItsmInputModel {
  customerName: string;
  projectId: string;
  isReseller: boolean = false;
  customerSearchBy?: string= 'name';
  otherCountriesSearch?: boolean = false;
  targetSystem: string;
  isLeadCustomer: boolean = false;
  title: string;
  enableTaxNumberSearch: boolean;
  enableLocalToCentralDeals: boolean;

  error: Error;
  selectedCustomer: Ms4CustomerModel = ms4CustomerModelFactory({});

  customersList: Ms4CustomerModel[] = [];
  customersListDisplay: Ms4CustomerModel[] = [];

  loading: boolean = true;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  totalCount: number = 0;
  matchFound: boolean = false;
  isAlternateName = false;
  isTaxNumber = false;
  isSoldToCustomerReseller = false;

  @Input()
  searchText: string = "";

  static showFindCustomerITSMDialogModal(dialogService: DialogService, input: FindCustomerItsmInputModel):
    Promise<any> {
    return dialogService.showDialog(FindCustomerITSMDialogComponent, input);
  }

  constructor(
    public dialogService: DialogService,
    private customerService: CustomerService,
    private appSettingsService: AppSettingsService,
  ) {
    super(dialogService);

    this.result = {selectedCustomerInfo: null, customerSearchBy:this.customerSearchBy,customerName:this.searchText};
  }

  cancel(): void {
    this.result = {selectedCustomerInfo: null, customerSearchBy:this.customerSearchBy,customerName:this.searchText};
    this.close();
  }

  @handleErrors()
  async selectCustomerComplete() {
    this.result = { selectedCustomerInfo: {...this.selectedCustomer}, customerSearchBy:this.customerSearchBy,customerName:this.searchText};
    this.close();
  }

  private adjustListHelper() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.customersListDisplay = (this.matchFound && start !== 0 && this.customersList.length) ?
      [
        this.customersList[0],
        ...this.customersList.slice(start, end),
      ] :
      this.customersList.slice(start, end);
  }
  private isAlternateNamePresent() {
    if (this.customersList.length) {
      for (let customer of this.customersList) {
        if (customer.alternateCustomerName) {
          this.isAlternateName = true;
          break;
        }
        else {
          this.isAlternateName = false;
        }
      }
    }
  }
  private isTaxNumberPresent() {
    if (this.customersList.length) {
      for (let customer of this.customersList) {
        if (customer.taxNumber || customer.taxNumberXL) {
          this.isTaxNumber = true;
          break;
        }
        else {
          this.isTaxNumber = false;
        }
      }
    }
  }

  @toggleLoading()
  @handleErrors()
  async loadData() {
    const featurePermissions = await this.appSettingsService.getFeaturePermissionsList(["enableTaxNumberSearch", "enableLocalToCentralDeals"]);
    [this.enableTaxNumberSearch, this.enableLocalToCentralDeals] = [featurePermissions["enableTaxNumberSearch"], featurePermissions["enableLocalToCentralDeals"]];
    this.loading = true;
    const customersListRaw =
      await this.customerService.listMs4Customers(this.searchText, this.projectId, this.isReseller, this.customerSearchBy, this.isLeadCustomer, this.otherCountriesSearch);
    this.customersList = customersListRaw.rows;
    this.matchFound = customersListRaw.matchFound;
    this.totalCount = this.customersList.length;
    this.adjustListHelper();

    if (this.enableTaxNumberSearch) {
      this.isAlternateNamePresent();
      this.isTaxNumberPresent();
    }

    this.recalculateMaxPages();
    if (this.matchFound || customersListRaw.rows.length === 1) {
      this.selectedCustomer = this.customersList[0];
    } else if ( // Current match is not in returned result set
      this.selectedCustomer.bpid &&
      !this.customersList.find(it => it.bpid === this.selectedCustomer.bpid)
    ) {
      this.selectedCustomer = ms4CustomerModelFactory({});
    }
    this.loading = false;
  }
  selectedRadioButton(type: string) {
    this.customerSearchBy = type;
    this.searchText = '';
  }

  SoldToInOtherCountrySearchCheckBox(type) {
    this.otherCountriesSearch = type.target.checked;
  }
  SoldToInOtherCountrySearchHelp() {
    SearchSoldtoInOtherCountriesInfoComponent.show(this.dialogService, { searchOptions: this.enableLocalToCentralDeals });
  }

  changePlaceholder() {
    if (this.enableTaxNumberSearch) {
      if (this.customerSearchBy === 'name') {
        return `Enter ${this.title} Name`
      } else if (this.customerSearchBy === 'bpid') {
        return `Enter ${this.title} BPID`
      } else {
        return "Enter Tax Number"
      }
    }
    else {
      return null
    }
  }

  changeMessage() {
    if (!this.searchText) {
      return "Please enter the search text"
    }
    else {
      return `There are no ${this.title} to display.`
    }
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    this.searchText = this.customerName;
    this.loading = true;
    if (this.isLeadCustomer) {
      this.title = "Lead Customer";
    } else if (!this.isReseller) {
      switch (this.targetSystem) {
        case "pmpsc":
        case TARGET_SYSTEM.SELL_WITH:
        case TARGET_SYSTEM.BILL_THROUGH:
        case TARGET_SYSTEM.MIGRATION_SELL_WITH:
        case TARGET_SYSTEM.MIGRATION_BILL_THROUGH:
        case TARGET_SYSTEM.PMPS_SELL_WITH:
        case TARGET_SYSTEM.PMPS_BILL_THROUGH:
        case TARGET_SYSTEM.MCS_BILL_THROUGH:
        case TARGET_SYSTEM.MCS_SELL_WITH:
        case TARGET_SYSTEM.CHANNEL_MCS_SELL_THROUGH:  
          this.title = "Sold-To";
          break;
        case "daas":
        case "midaas":
          this.title = "Customer";
          break;
        default: this.title = "End Customer";
      }
    } else {
      switch (this.targetSystem) {
        case "pmpsc":
        case TARGET_SYSTEM.SELL_WITH:
        case TARGET_SYSTEM.BILL_THROUGH:
        case TARGET_SYSTEM.MIGRATION_SELL_WITH:
        case TARGET_SYSTEM.MIGRATION_BILL_THROUGH:
        case TARGET_SYSTEM.PMPS_SELL_WITH:
        case TARGET_SYSTEM.PMPS_BILL_THROUGH:
        case TARGET_SYSTEM.MCS_BILL_THROUGH:
        case TARGET_SYSTEM.MCS_SELL_WITH:
        case TARGET_SYSTEM.CHANNEL_MCS_SELL_THROUGH:   
          this.title = "Reseller/Vendor";
          break;
        case "daas":
        case "midaas":
          this.title = "Reseller";
          break;
        default: this.title = "Reseller/SoldTo";
      }
    }

    if (this.targetSystem === "pmpsp" || this.targetSystem === "mpflexr" || 
    this.targetSystem === TARGET_SYSTEM.SELL_THROUGH || this.targetSystem === TARGET_SYSTEM.CHANNEL_MCS_SELL_THROUGH || this.targetSystem === TARGET_SYSTEM.MIGRATION_SELL_THROUGH ||
    this.targetSystem === TARGET_SYSTEM.PMPS_SELL_THROUGH || this.targetSystem === TARGET_SYSTEM.CHANNEL_DAAS ||
    this.targetSystem === TARGET_SYSTEM.MIGRATION_CHANNEL_DAAS || this.targetSystem === TARGET_SYSTEM.MCS_SELL_THROUGH) {
      if (this.isReseller) {
        this.isSoldToCustomerReseller = true;
      }
    } else if (!this.isReseller) {
      this.isSoldToCustomerReseller = true;
    }
    await this.loadData();
  }

  public customerSelectedEvent(bpid) {
    this.selectedCustomer = this.customersList.find(c => c.bpid === bpid);
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.totalCount / (+this.pageSize));
  }

  async onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        this.adjustListHelper();
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        if (!event["value"].length) {
          this.customersListDisplay = [];
          this.searchText = "";
        } else {
          this.searchText = event["value"];
        } 
        if(this.error) {
          this.customersListDisplay = []
        }
        this.loading = true;
        await this.loadData();
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        this.adjustListHelper();
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
  }
}
